import React, { useRef, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import {
  Paperclip,
  X,
  Upload,
  Download,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useTran } from "@/hooks";
import { useCallForHelpStore } from "../hooks";
import { useAuthStore } from "@/features/auth";
import { ConfirmDialog } from "@/components/common";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_MIME = [
  "image/png",
  "image/jpeg",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const AttachmentPanel = observer(() => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const companyId = user?.companyId;
  const store = useCallForHelpStore();
  const fileInputRef = useRef(null);

  const [fileToDelete, setFileToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    if (store.attachmentResetKey === 0) return;
    setValidationErrors([]);
  }, [store.attachmentResetKey]);

  const files = store.evidenceFiles;

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    let i = 0,
      size = bytes;
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024;
      i++;
    }
    return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  };

  const validateFiles = (selectedFiles) => {
    const valid = [],
      errors = [];
    for (const file of selectedFiles) {
      if (file.size > MAX_FILE_SIZE) {
        errors.push(
          `"${file.name}" ${t("exceeds the 10 MB limit")} (${formatFileSize(file.size)})`,
        );
        continue;
      }
      if (!ALLOWED_MIME.includes(file.type)) {
        errors.push(`"${file.name}" ${t("is not a supported file type")}`);
        continue;
      }
      valid.push(file);
    }
    return { valid, errors };
  };

  const handleFileSelect = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length || !companyId) return;
    const { valid, errors } = validateFiles(selected);
    setValidationErrors(errors);
    if (valid.length > 0) await store.uploadAttachment(companyId, valid);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files || []);
    if (!dropped.length || !companyId) return;
    const { valid, errors } = validateFiles(dropped);
    setValidationErrors(errors);
    if (valid.length > 0) store.uploadAttachment(companyId, valid);
  };

  const handleDeleteClick = (fileItem) => setFileToDelete(fileItem);
  const handleConfirmDelete = async () => {
    if (!fileToDelete) return;
    setDeleting(true);
    try {
      await store.deleteAttachment(fileToDelete.fileId);
      setFileToDelete(null);
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = async (fileItem) => {
    try {
      await store.downloadAttachment(fileItem);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const dismissError = (index) =>
    setValidationErrors((prev) => prev.filter((_, i) => i !== index));

  return (
    <>
      <TooltipProvider delayDuration={300}>
        {/* ── Panel Shell ── */}
        <div
          className="w-64 shrink-0 rounded-lg border p-4 flex flex-col gap-3
                     bg-white        border-gray-200
                     dark:bg-gray-900 dark:border-gray-700"
        >
          {/* ── Title ── */}
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {t("Attachments")}
          </p>

          {/* ── Drop Zone ── */}
          <div
            onClick={() => !store.isUploading && fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className={`flex flex-col items-center justify-center gap-2
                        border-2 border-dashed rounded-lg p-6 transition-colors
                        ${
                          store.isUploading
                            ? "border-blue-300 bg-blue-50 cursor-not-allowed dark:border-blue-700 dark:bg-blue-950"
                            : "border-gray-300 cursor-pointer hover:border-[#2d3a8c] hover:bg-blue-50 dark:border-gray-600 dark:hover:border-[#3d4fba] dark:hover:bg-blue-950"
                        }`}
          >
            {store.isUploading ? (
              <Loader2 className="w-7 h-7 animate-spin text-[#2d3a8c] dark:text-[#3d4fba]" />
            ) : (
              <Upload className="w-7 h-7 text-gray-300 dark:text-gray-600" />
            )}
            <p
              className="text-xs font-medium text-center
                          text-gray-500 dark:text-gray-400"
            >
              {store.isUploading
                ? t("Uploading...")
                : t("Click or drag & drop")}
            </p>
            <p className="text-xs text-center text-gray-400 dark:text-gray-500">
              PNG, JPG, PDF, DOCX
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {t("Max 10 MB per file")}
            </p>
          </div>

          {/* ── Hidden Input ── */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
            onChange={handleFileSelect}
            className="hidden"
            disabled={store.isUploading}
          />

          {/* ── Validation Errors ── */}
          {validationErrors.length > 0 && (
            <ul className="space-y-1">
              {validationErrors.map((err, idx) => (
                <li
                  key={idx}
                  className="flex items-start justify-between gap-1
                             rounded-md border px-2 py-1.5
                             bg-red-50  border-red-200
                             dark:bg-red-950/40 dark:border-red-800"
                >
                  <div className="flex items-start gap-1 min-w-0">
                    <AlertCircle
                      className="w-3.5 h-3.5 shrink-0 mt-0.5
                                 text-red-500 dark:text-red-400"
                    />
                    <p
                      className="text-xs break-words
                                  text-red-600 dark:text-red-400"
                    >
                      {err}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => dismissError(idx)}
                    className="shrink-0 text-red-400 hover:text-red-600
                               dark:text-red-500 dark:hover:text-red-300"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* ── File List ── */}
          <div className="flex-1 overflow-hidden flex flex-col gap-2 min-h-0">
            {files.length > 0 ? (
              <>
                <p className="text-xs shrink-0 text-gray-500 dark:text-gray-400">
                  {files.length} {t("file(s) attached")}
                </p>

                <ul
                  className="max-h-48 overflow-y-auto space-y-1.5
                               scrollbar-thin
                               scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600"
                >
                  {files.map((file) => (
                    <li
                      key={file.fileId}
                      className="flex items-center justify-between gap-2
                                 px-2 py-1.5 rounded-md border
                                 bg-gray-50  border-gray-200
                                 dark:bg-gray-800 dark:border-gray-700"
                    >
                      {/* File Info */}
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Paperclip
                          className="w-3.5 h-3.5 shrink-0
                                     text-gray-400 dark:text-gray-500"
                        />
                        <div className="min-w-0">
                          {/* Filename + Tooltip */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <p
                                className="truncate text-xs font-medium cursor-default
                                            text-gray-700 dark:text-gray-200"
                              >
                                {file.name}
                              </p>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              align="start"
                              className="max-w-xs break-all text-xs"
                            >
                              {file.name}
                            </TooltipContent>
                          </Tooltip>

                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => handleDownload(file)}
                          title={t("Download")}
                          className="transition-colors
                                     text-gray-400 hover:text-[#2d3a8c]
                                     dark:text-gray-500 dark:hover:text-[#3d4fba]"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(file)}
                          title={t("Remove")}
                          className="transition-colors
                                     text-gray-400 hover:text-red-500
                                     dark:text-gray-500 dark:hover:text-red-400"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <div
                className="flex-1 flex items-center justify-center
                              rounded-lg border border-dashed p-4
                              border-gray-200 dark:border-gray-700"
              >
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {t("No files attached yet")}
                </p>
              </div>
            )}
          </div>
        </div>
      </TooltipProvider>

      {/* ── Confirm Delete Dialog ── */}
      <ConfirmDialog
        open={!!fileToDelete}
        onOpenChange={(open) => {
          if (!open) setFileToDelete(null);
        }}
        title={t("Delete attachment")}
        description={
          fileToDelete
            ? `${t("Are you sure you want to delete")} "${fileToDelete.name}"? ${t("This action cannot be undone.")}`
            : ""
        }
        confirmLabel={t("Delete")}
        cancelLabel={t("Cancel")}
        onConfirm={handleConfirmDelete}
        loading={deleting}
        tone="danger"
      />
    </>
  );
});

export default AttachmentPanel;
